/* eslint-disable */

import { getProductRechargeData } from './subscription-helpers.js'

/**
 * @typedef {Object} SubscriptionData
 * @property {Object} shopifyConfig
 * @property {string} shopifyConfig.currency
 * @property {string} shopifyConfig.identifier
 * @property {string} shopifyConfig.template
 * @property {number} shopifyConfig.market_id
 * @property {boolean} shopifyConfig.show_powered_by_recharge
 *
 * @property {Object.<string, {
 *   unit: string,
 *   discounted: string,
 *   sellingPlans: Object.<string, {
 *     price: string,
 *     discountType: string,
 *     discountValue: number
 *   }>
 * }>} formattedPrices - keyed by variant ID
 *
 * @property {Object} configs
 * @property {Object} configs.subscription_widget_v2
 * @property {Array} configs.subscription_widget_v2.widget_configs
 * @property {Object} configs.subscription_widget_v2.default_widget_config
 * @property {number|null} configs.subscription_widget_v2.default_widget_config.trigger_type
 * @property {null|*} configs.subscription_widget_v2.default_widget_config.trigger_values
 * @property {null|*} configs.subscription_widget_v2.default_widget_config.product_template_ids
 * @property {number} configs.subscription_widget_v2.default_widget_config.widget_flow_id
 * @property {string} configs.subscription_widget_v2.default_widget_config.widget_flow_name
 * @property {Array<{
 *   ab_split_id: any,
 *   ab_split_name: any,
 *   percentage: number,
 *   display_configs: Array<{
 *     config_information: {
 *       benefits: string,
 *       badgeText: string,
 *       config_id: number,
 *       text_color: string,
 *       brand_color: string,
 *       displayMode: string,
 *       onetimeLabel: string,
 *       benefitsImage: any,
 *       border_radius: string,
 *       learnMoreText: string,
 *       badge_bg_color: string,
 *       frequencyLabel: string,
 *       badge_text_color: string,
 *       defaultSelection: string,
 *       learnMoreContent: string,
 *       onetimeDrawbacks: string,
 *       selected_bg_color: string,
 *       staticPricingText: string,
 *       dynamicPricingText: string,
 *       selected_text_color: string,
 *       brand_contrast_color: string,
 *       firstSelectionOption: string,
 *       frequencyDisplayType: string,
 *       showStrikethroughPrice: boolean,
 *       frequencyDisplayContent: string,
 *       frequencyIntervalTranslation: Object<string, string>,
 *       subscriptionWithDiscountLabel: string,
 *       subscriptionWithoutDiscountLabel: string
 *     },
 *     external_market_id: any
 *   }>,
 *   plan_configs: any
 * }>} configs.subscription_widget_v2.default_widget_config.ab_splits
 */

/**
 * MitoQ Subscription Widget
 * Custom implementation to display and manage subscription options
 */
class MitoqSubscriptionWidget extends HTMLElement {
  constructor() {
    super()
    this.subscriptionData = null
    this.productForm = null
    this._perfStart = null
    this._pricePaintLogged = false
    this._addToCartFallbackTimer = null
    this._perfDebugEnabled =
      typeof window !== 'undefined' &&
      (window.__MITOQ_SUB_DEBUG__ === true ||
        new URLSearchParams(window.location.search).get('mitoqSubDebug') ===
          '1')
    // Bind methods to ensure correct 'this' context
    this.handleVariantSelection = this.handleVariantSelection.bind(this)
    this.updateButtonSelection = this.updateButtonSelection.bind(this)
    this.handleProductFormSubmit = this.handleProductFormSubmit.bind(this)
    // this.connectedCallback()
  }

  perfLog(message, meta = null) {
    if (!this._perfDebugEnabled) return

    const elapsed =
      this._perfStart !== null
        ? (performance.now() - this._perfStart).toFixed(1)
        : 'n/a'

    if (meta) {
      console.log(`[MitoQSub][perf +${elapsed}ms] ${message}`, meta)
      return
    }

    console.log(`[MitoQSub][perf +${elapsed}ms] ${message}`)
  }

  clearAddToCartFallbackTimer() {
    if (this._addToCartFallbackTimer === null) return

    window.clearTimeout(this._addToCartFallbackTimer)
    this._addToCartFallbackTimer = null
  }

  hasPendingB2BAddToCartDelay() {
    // Legacy delayed B2B add-to-cart flow has been removed.
    return false
  }

  startAddToCartFallbackTimer(reason = 'unknown') {
    if (this._addToCartFallbackTimer !== null || this.hasPendingB2BAddToCartDelay()) {
      return
    }

    this._addToCartFallbackTimer = window.setTimeout(() => {
      this._addToCartFallbackTimer = null
      this.enableAddToCartButton()
      this.perfLog('enableAddToCartButton fallback finished (600ms)', {
        reason,
      })
    }, 600)
  }

  getSelectedButtonState() {
    const selectedButton = this.getSelectedButtonForSubmit()
    const variantId = selectedButton?.getAttribute('data-variant-id') || null
    const isSubscription =
      selectedButton?.closest('.variant-buttons-subs') !== null

    return {
      selectedButton,
      variantId,
      isSubscription,
      method: isSubscription ? 'subscription' : 'onetime',
    }
  }

  isSelectedPurchaseReady() {
    const { selectedButton, variantId, isSubscription } =
      this.getSelectedButtonState()

    if (!selectedButton) return false
    if (!isSubscription) return true

    return !!this.resolveSellingPlanIdForVariant(variantId)
  }

  syncAddToCartReadiness(reason = 'unknown') {
    if (this.hasPendingB2BAddToCartDelay()) {
      this.perfLog('add-to-cart readiness deferred by B2B flow', { reason })
      return false
    }

    const { variantId, method } = this.getSelectedButtonState()
    const isReady = this.isSelectedPurchaseReady()

    this.perfLog('add-to-cart readiness check', {
      reason,
      ready: isReady,
      method,
      variantId,
    })

    if (isReady) {
      this.clearAddToCartFallbackTimer()
      this.enableAddToCartButton()
      return true
    }

    this.disableAddToCartButton()
    this.startAddToCartFallbackTimer(reason)
    return false
  }

  connectedCallback() {
    this._perfStart = performance.now()
    this.perfLog('connectedCallback start', {
      hydrationEnabled: this.isPricingHydrationEnabled(),
      productUrl: this.getAttribute('data-product-url') || null,
    })
    console.log('🔄 MitoQ Subscription: Starting initialization')

    if (this.isPricingHydrationEnabled()) {
      this.setPricingHydratedState(false)
      this.perfLog('set data-pricing-hydrated=false')
    }

    // Disable add-to-cart button during initialization
    this.disableAddToCartButton()

    // Apply user type logic (B2B/wholesale and product option logic)
    this.applyUserTypeLogic()
    // Initialize when element is connected to DOM
    this.fetchSubscriptionData()
    this.initializeEventListeners()

    // Validate URL variant parameter and redirect if needed
    this.validateAndRedirectVariant()

    // After everything is set up, handle auto-selection
    const urlParams = new URLSearchParams(window.location.search)
    const urlVariantId = urlParams.get('variant')

    if (urlVariantId) {
      // Auto-select variant from URL
      this.autoSelectVariant({ variantId: urlVariantId })
    } else {
      // Ensure any visually selected button is also programmatically selected
      const preSelectedButton = this.querySelector(
        'button[data-selected="true"]'
      )
      const isB2B = this.getAttribute('data-is-b2b') === 'true'

      if (preSelectedButton && !isB2B) {
        this.performVariantSelection(preSelectedButton, { silent: false })
      } else if (isB2B) {
        // B2B can only buy one-time variants.
        this.autoSelectVariant({ method: 'onetime', force: true })
      } else {
        // Auto-select default variant (e.g., first one-time for B2B)
        this.autoSelectVariant()
      }
    }

    // Handle B2B specific logic for selling plan clearing/form sync.
    this.handleB2BAutoSelection()

    // Show default badges for both methods on initial load
    this.showDefaultMethodBadges()
    this.syncAddToCartReadiness('connectedCallback complete')

    // --- Listen for sticky ATC sync events ---
    window.addEventListener('mitoq-sticky-atc-sync', (e) => {
      const { method, variantId } = e.detail || {}

      // Use centralized auto-selection function
      if (method && variantId) {
        this.autoSelectVariant({ method, variantId })
      } else if (method) {
        this.autoSelectVariant({ method })
      } else if (variantId) {
        this.autoSelectVariant({ variantId })
      }
    })
  }

  disconnectedCallback() {
    // Cleanup when element is removed from DOM
    this.clearAddToCartFallbackTimer()
    this.removeEventListeners()
  }

  isPricingHydrationEnabled() {
    return this.getAttribute('data-enable-pricing-hydration') === 'true'
  }

  setPricingHydratedState(isHydrated) {
    if (!this.isPricingHydrationEnabled()) return
    this.setAttribute('data-pricing-hydrated', isHydrated ? 'true' : 'false')
    this.perfLog(`set data-pricing-hydrated=${isHydrated ? 'true' : 'false'}`)
  }

  /**
   * Get allowed variants for a specific user type
   * @param {string} userType - The user type (e.g., 'b2b', 'regular')
   * @returns {Array<string>} Array of allowed variant patterns
   */
  getAllowedVariants(userType = 'b2b') {
    try {
      const allowedVariants = JSON.parse(
        this.getAttribute('data-allowed-variants') || '[]'
      )
      return allowedVariants
    } catch (e) {
      console.error('Failed to parse allowed variants:', e)
      return []
    }
  }

  /**
   * Validate if a variant is accessible for a specific user type
   * @param {string} variantName - The variant name to validate
   * @param {string} userType - The user type
   * @returns {boolean} True if variant is allowed
   */
  validateVariantAccess(variantName, userType = 'b2b') {
    const allowedVariants = this.getAllowedVariants(userType)

    // If no restrictions are set, allow all variants
    if (allowedVariants.length === 0) {
      return true
    }

    return allowedVariants.some((pattern) => {
      try {
        return new RegExp(pattern, 'i').test(variantName)
      } catch (e) {
        // If regex fails, try exact string match
        return variantName.toLowerCase().includes(pattern.toLowerCase())
      }
    })
  }

  /**
   * Find the first allowed variant for a user type
   * @param {string} userType - The user type
   * @returns {Object|null} Object with variantId and button, or null if none found
   */
  findFirstAllowedVariant(userType = 'b2b') {
    const allButtons = this.querySelectorAll('button[data-variant-id]')

    for (const button of allButtons) {
      const variantName = button.getAttribute('data-variant-name') || ''
      if (this.validateVariantAccess(variantName, userType)) {
        return {
          variantId: button.getAttribute('data-variant-id'),
          button: button,
        }
      }
    }

    return null
  }

  /**
   * Validate URL variant parameter and redirect if needed
   */
  validateAndRedirectVariant() {
    const urlParams = new URLSearchParams(window.location.search)
    const urlVariantId = urlParams.get('variant')

    if (!urlVariantId) {
      return // No variant in URL, nothing to validate
    }

    const userType = this.getAttribute('data-user-type') || 'regular'
    const allowedVariants = this.getAllowedVariants(userType)

    // If no restrictions are set, allow the variant
    if (allowedVariants.length === 0) {
      return
    }

    // Find the button with the URL variant ID
    const targetButton = this.querySelector(
      `button[data-variant-id='${urlVariantId}']`
    )

    if (!targetButton) {
      console.warn(`Variant button not found for ID: ${urlVariantId}`)
      return
    }

    const variantName = targetButton.getAttribute('data-variant-name') || ''

    // Check if the variant is allowed for this user type
    if (!this.validateVariantAccess(variantName, userType)) {
      console.log(
        `Access denied for user type '${userType}' to variant '${variantName}' (ID: ${urlVariantId})`
      )

      // Find the first allowed variant
      const allowedVariant = this.findFirstAllowedVariant(userType)

      if (allowedVariant) {
        console.log(
          `Redirecting to allowed variant: ${allowedVariant.variantId}`
        )

        // Auto-select the allowed variant
        this.autoSelectVariant({ variantId: allowedVariant.variantId })
      } else {
        console.error(`No allowed variants found for user type: ${userType}`)

        // Remove variant parameter and let default logic handle it
        const newUrl = new URL(window.location.href)
        newUrl.searchParams.delete('variant')
        window.history.replaceState({}, '', newUrl)
      }
    }
  }

  /**
   * Initialize event listeners for variant buttons
   */
  initializeEventListeners() {
    // Listen for both subscription and one-time buttons
    const subsButtons = this.querySelectorAll(
      '.variant-buttons-subs button[data-variant-id]'
    )
    const onceButtons = this.querySelectorAll(
      '.variant-buttons-once button[data-variant-id]'
    )

    subsButtons.forEach((button) => {
      button.addEventListener('click', this.handleVariantSelection)
    })
    onceButtons.forEach((button) => {
      button.addEventListener('click', this.handleVariantSelection)
    })

    this.querySelectorAll('.ms-method').forEach((method) => {
      this.handleMethodSelection(method)
    })

    this.productForm = this.getProductForm()
    if (this.productForm && this.shouldControlFormSubmit()) {
      this.productForm.addEventListener(
        'submit',
        this.handleProductFormSubmit,
        true
      )
    }
  }

  handleMethodSelection(method) {
    method.addEventListener('click', (event) => {
      // If the click originated from a variant button, do nothing
      if (event.target.closest('button[data-variant-id]')) {
        return
      }
      this.querySelectorAll('.ms-method').forEach((meth) => {
        const isSub = meth.classList.contains('mitoq-subscription')
        // Only restore the default if this is not the one being activated
        if (isSub && meth !== event.currentTarget) {
          this.updateScheduleLabel(null, meth, false)
        }
        meth.setAttribute('data-is-active', 'false')
      })
      event.currentTarget.setAttribute('data-is-active', 'true')

      // Auto-select a concrete visible variant in the activated method
      const methodType = event.currentTarget.classList.contains(
        'mitoq-subscription'
      )
        ? 'subscription'
        : 'onetime'
      const buttonSelector =
        methodType === 'subscription'
          ? '.variant-buttons-subs button[data-variant-id]'
          : '.variant-buttons-once button[data-variant-id]'

      const visibleButtons = Array.from(
        event.currentTarget.querySelectorAll(buttonSelector)
      ).filter((button) => button.style.display !== 'none')

      const selectedVisibleButton = visibleButtons.find(
        (button) => button.getAttribute('data-selected') === 'true'
      )
      const targetButton = selectedVisibleButton || visibleButtons[0] || null

      if (targetButton) {
        this.performVariantSelection(targetButton, { silent: false })
      } else {
        this.autoSelectVariant({ method: methodType, force: true })
      }

      // After switching, show default badges for closed methods only
      // Don't call showDefaultMethodBadges here as it interferes with the active method's discount state
      // The active method's discount state is handled by updateOneTimePriceFields/updatePriceFields
    })
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    const subsButtons = this.querySelectorAll(
      '.variant-buttons-subs button[data-variant-id]'
    )
    const onceButtons = this.querySelectorAll(
      '.variant-buttons-once button[data-variant-id]'
    )

    subsButtons.forEach((button) => {
      button.removeEventListener('click', this.handleVariantSelection)
    })
    onceButtons.forEach((button) => {
      button.removeEventListener('click', this.handleVariantSelection)
    })

    if (this.productForm) {
      this.productForm.removeEventListener(
        'submit',
        this.handleProductFormSubmit,
        true
      )
    }
  }

  isWidgetVisible(widget = this) {
    if (!widget || !widget.isConnected) return false
    const style = window.getComputedStyle(widget)
    return style.display !== 'none' && style.visibility !== 'hidden'
  }

  getPrimaryWidgetForForm() {
    const formId = this.getAttribute('data-form-id')
    if (!formId) return null

    const widgets = Array.from(
      document.querySelectorAll(`mitoq-subscription[data-form-id="${formId}"]`)
    )

    if (widgets.length === 0) return null

    const visibleWidgets = widgets.filter((widget) => this.isWidgetVisible(widget))
    const candidates = visibleWidgets.length > 0 ? visibleWidgets : widgets

    const tabbedWidget = candidates.find((widget) =>
      widget.classList.contains('mitoq-subscription--tabbed')
    )

    return tabbedWidget || candidates[0]
  }

  shouldControlFormSubmit() {
    const primaryWidget = this.getPrimaryWidgetForForm()
    return !primaryWidget || primaryWidget === this
  }

  getProductForm() {
    const formId = this.getAttribute('data-form-id')
    if (!formId) return null

    return document.querySelector(
      `form[id="${formId}"], form[data-form-id="${formId}"]`
    )
  }

  getSelectedButtonForSubmit() {
    const activeMethods = Array.from(
      this.querySelectorAll('.ms-method[data-is-active="true"]')
    ).filter((method) => method.style.display !== 'none')

    for (const activeMethod of activeMethods) {
      const selectedInActive = activeMethod.querySelector(
        'button[data-variant-id][data-selected="true"]:not([style*="display: none"])'
      )
      if (selectedInActive) return selectedInActive

      const firstVisibleInActive = activeMethod.querySelector(
        'button[data-variant-id]:not([style*="display: none"])'
      )
      if (firstVisibleInActive) return firstVisibleInActive
    }

    return (
      this.querySelector(
        'button[data-variant-id][data-selected="true"]:not([style*="display: none"])'
      ) ||
      this.querySelector('button[data-variant-id]:not([style*="display: none"])')
    )
  }

  getOrCreateSellingPlanInput(productForm) {
    if (!productForm) return null

    const inputs = Array.from(
      productForm.querySelectorAll('input[name="selling_plan"]')
    )

    let input = inputs[0] || null
    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'selling_plan'
      input.value = ''
      productForm.appendChild(input)
    }

    // Keep a single source of truth for selling_plan in this form.
    inputs.slice(1).forEach((extraInput) => extraInput.remove())

    return input
  }

  resolveSellingPlanIdForVariant(variantId) {
    if (!variantId || !this.subscriptionData || !this.subscriptionData.formattedPrices) {
      return null
    }

    const priceData = this.subscriptionData.formattedPrices[variantId]
    if (!priceData || !priceData.sellingPlans) return null

    const sellingPlanIds = Object.keys(priceData.sellingPlans)
    return sellingPlanIds.length > 0 ? sellingPlanIds[0] : null
  }

  syncSellingPlanInputForSelection(selectedButton = null, submitEvent = null) {
    const productForm = this.getProductForm()
    if (!productForm) return false

    const button = selectedButton || this.getSelectedButtonForSubmit()
    if (!button) return false

    const sellingPlanInput = this.getOrCreateSellingPlanInput(productForm)
    if (!sellingPlanInput) return false

    const isB2B = this.getAttribute('data-is-b2b') === 'true'
    const isSubscriptionSelection =
      button.closest('.variant-buttons-subs') !== null

    // For B2B and all one-time selections, selling_plan must be omitted.
    if (isB2B || !isSubscriptionSelection) {
      sellingPlanInput.value = ''
      sellingPlanInput.disabled = true
      sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }))
      return true
    }

    const variantId = button.getAttribute('data-variant-id')
    const sellingPlanId = this.resolveSellingPlanIdForVariant(variantId)
    if (!sellingPlanId) {
      if (submitEvent) {
        submitEvent.preventDefault()
        submitEvent.stopPropagation()
      }
      console.warn(
        'MitoQ Subscription: blocked submit because selling plan is unavailable for selected subscription variant.'
      )
      return false
    }

    sellingPlanInput.disabled = false
    sellingPlanInput.value = String(sellingPlanId)
    sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }))
    return true
  }

  handleProductFormSubmit(event) {
    if (!this.shouldControlFormSubmit()) {
      return
    }

    const selectedButton = this.getSelectedButtonForSubmit()
    if (!selectedButton) return

    const variantId = selectedButton.getAttribute('data-variant-id')
    const formId = this.getAttribute('data-form-id')
    if (!variantId || !formId) return

    // Keep form variant in lockstep with visible widget selection at submit time.
    MitoqSubscriptionWidget.updateProductForm(formId, variantId)

    this.syncSellingPlanInputForSelection(selectedButton, event)
  }

  /**
   * Handle variant button selection
   * @param {Event} event - Click event from variant button
   */
  handleVariantSelection(event) {
    const clickedButton = event.currentTarget
    const variantId = clickedButton.getAttribute('data-variant-id')
    const variantName = clickedButton.getAttribute('data-variant-name') || ''
    const formId = this.getAttribute('data-form-id')

    if (!variantId || !formId) {
      console.error('Missing variant ID or form ID')
      return
    }

    // Validate variant access for user type
    const userType = this.getAttribute('data-user-type') || 'regular'
    if (!this.validateVariantAccess(variantName, userType)) {
      console.warn(
        `Access denied for user type '${userType}' to variant '${variantName}' (ID: ${variantId})`
      )
      event.preventDefault()
      return
    }

    // Update the URL with the new variant id (without reloading)
    const url = new URL(window.location.href)
    url.searchParams.set('variant', variantId)
    window.history.replaceState({}, '', url)

    // Update button selection states (only in the relevant group)
    this.updateButtonSelection(clickedButton)

    // Determine if this is a subscription or one-time button
    const isSubs = clickedButton.closest('.variant-buttons-subs') !== null
    const isOnce = clickedButton.closest('.variant-buttons-once') !== null
    let container = null
    if (isSubs) {
      container = this.querySelector('.mitoq-subscription')
      this.updatePriceFields(clickedButton, variantId, container)
      // Update frequency heading
      this.updateFreqHeading(clickedButton, container)
      // Update schedule label
      this.updateScheduleLabel(clickedButton, container, true)
    } else if (isOnce) {
      container = this.querySelector('.mitoq-one-time')
      this.updateOneTimePriceFields(clickedButton, container)
    }

    // Update the image
    this.updateImage(clickedButton)

    // Find and update the product form
    MitoqSubscriptionWidget.updateProductForm(formId, variantId)
    this.syncSellingPlanInputForSelection(clickedButton)

    // --- Dispatch sync event ---
    const methodType = isSubs ? 'subscription' : 'onetime'
    const syncEvent = new CustomEvent('mitoq-subscription-sync', {
      detail: { method: methodType, variantId },
    })
    window.dispatchEvent(syncEvent)

    this.syncAddToCartReadiness('user variant selection')
  }

  // eslint-disable-next-line class-methods-use-this
  updateImage(selectedButton) {
    const imageUrl = selectedButton.getAttribute('data-image-url')
    const parentHeading = selectedButton
      .closest('.ms-method')
      .querySelector('.ms-heading--image img')
    if (parentHeading) parentHeading.src = imageUrl
  }

  /**
   * Update visual selection state of variant buttons (only in the relevant group)
   * @param {HTMLElement} selectedButton - The button that was clicked
   */
  updateButtonSelection(selectedButton) {
    // Use 'this' to satisfy linter
    if (!this) return
    // Find the closest button group
    const buttonGroup = selectedButton.closest(
      '.variant-buttons-subs, .variant-buttons-once'
    )
    if (!buttonGroup) return
    const allButtons = buttonGroup.querySelectorAll('button[data-variant-id]')
    allButtons.forEach((button) => {
      button.setAttribute('data-selected', 'false')
    })
    selectedButton.setAttribute('data-selected', 'true')
  }

  /**
   * Update the price fields in the DOM for the selected subscription variant
   * @param {HTMLElement} clickedButton
   * @param {string} variantId
   * @param {HTMLElement} container
   */
  updatePriceFields(clickedButton, variantId, container) {
    if (!this.subscriptionData || !this.subscriptionData.formattedPrices) return
    const priceData = this.subscriptionData.formattedPrices[variantId]
    if (!priceData) return

    // Update price fields (subscription only)
    const originalPriceEl = container.querySelector(
      '[data-original-price][data-subscription-original-price]'
    )
    const discountedPriceEl = container.querySelector(
      '[data-discounted-price][data-subscription-discounted-price]'
    )
    const currencySymbol = this.dataset.currencySymbol || '$'
    const currencyCode = this.dataset.currency || ''
    function splitPrice(priceStr) {
      // If it's a number or a numeric string, parse and divide by 100
      let num = typeof priceStr === 'number' ? priceStr : parseFloat(priceStr)
      if (!Number.isNaN(num)) {
        // If it's an integer >= 100, assume it's cents
        if (Number.isInteger(num) && num >= 100) {
          num /= 100
        }
        // Special case for Euro prices - comma as decimal separator
        if ((currencySymbol || '') === '€') {
          return num.toFixed(2).replace('.', ',')
        }
        return num.toFixed(2)
      }
      // Fallback for legacy string prices (with $ etc)
      let value = priceStr ? priceStr.replace(/[^\d.,]/g, '') : ''

      value = value.replace(',', '.')
      num = parseFloat(value)
      if (Number.isNaN(num)) return ''

      // Special case for Euro prices - comma as decimal separator
      if ((currencySymbol || '') === '€') {
        return num.toFixed(2).replace('.', ',')
      }
      return num.toFixed(2)
    }
    if (originalPriceEl && priceData.unit) {
      const value = splitPrice(priceData.unit)
      originalPriceEl.innerHTML = `<span>${currencySymbol}</span><span>${value}</span><span>${currencyCode}</span>`
    }
    if (discountedPriceEl && priceData.discounted) {
      const value = splitPrice(priceData.discounted)
      discountedPriceEl.innerHTML = `<span>${currencySymbol}</span><span>${value}</span><span>${currencyCode}</span>`
    }

    // --- DISCOUNT BADGE LOGIC ---
    const badgeEl = container.querySelector('[data-badge]')
    let hasDiscount = false
    let discountText = ''
    if (priceData.sellingPlans) {
      for (const planId of Object.keys(priceData.sellingPlans)) {
        const plan = priceData.sellingPlans[planId]
        if (plan.discountValue > 0) {
          hasDiscount = true
          discountText = `Save ${plan.discountValue}%`
          break
        }
      }
    }
    if (badgeEl) badgeEl.textContent = hasDiscount ? discountText : ''
    container.setAttribute('data-has-discount', hasDiscount ? 'true' : 'false')

    if (!this._pricePaintLogged) {
      this._pricePaintLogged = true
      this.perfLog('first subscription price rendered', {
        variantId,
        hasDiscount,
      })
    }
  }

  /**
   * Update the price fields in the DOM for the selected one-time variant
   * @param {HTMLElement} clickedButton
   * @param {HTMLElement} container
   */
  updateOneTimePriceFields(clickedButton, container) {
    const originalPriceEl = container.querySelector(
      '[data-original-price][data-onetime-original-price]'
    )
    const discountedPriceEl = container.querySelector(
      '[data-discounted-price][data-onetime-discounted-price]'
    )
    const currencySymbol = this.dataset.currencySymbol || '$'
    const currencyCode = this.dataset.currency || ''
    function splitPrice(priceStr) {
      // If it's a number or a numeric string, parse and divide by 100
      let num = typeof priceStr === 'number' ? priceStr : parseFloat(priceStr)
      if (!Number.isNaN(num)) {
        // If it's an integer >= 100, assume it's cents
        if (Number.isInteger(num) && num >= 100) {
          num /= 100
        }
        // Special case for Euro prices - comma as decimal separator
        if ((currencySymbol || '') === '€') {
          return num.toFixed(2).replace('.', ',')
        }
        return num.toFixed(2)
      }
      // Fallback for legacy string prices (with $ etc)
      let value = priceStr ? priceStr.replace(/[^\d.,]/g, '') : ''
      value = value.replace(',', '.')
      num = parseFloat(value)
      if (Number.isNaN(num)) return ''

      // Special case for Euro prices - comma as decimal separator
      if ((currencySymbol || '') === '€') {
        return num.toFixed(2).replace('.', ',')
      }
      return num.toFixed(2)
    }
    const salePrice = clickedButton.getAttribute('data-variant-price')
    const compareAtPrice = clickedButton.getAttribute(
      'data-variant-price-compare'
    )
    // Set data-has-discount on .mitoq-one-time
    if (container) {
      if (compareAtPrice && Number(compareAtPrice) > Number(salePrice)) {
        container.setAttribute('data-has-discount', 'true')
        // Calculate and inject discount badge
        const badgeEl = container.querySelector('[data-one-time-badge]')
        if (badgeEl) {
          const discount = Math.round(
            (1 - Number(salePrice) / Number(compareAtPrice)) * 100
          )
          badgeEl.textContent = `Save ${discount}%`
        }
      } else {
        container.setAttribute('data-has-discount', 'false')
        // Clear badge
        const badgeEl = container.querySelector('[data-one-time-badge]')
        if (badgeEl) badgeEl.textContent = ''
      }
    }
    if (
      originalPriceEl &&
      compareAtPrice &&
      Number(compareAtPrice) > Number(salePrice)
    ) {
      // Show compare-at as strikethrough, sale price as discounted
      const compareValue = splitPrice(compareAtPrice)
      originalPriceEl.innerHTML = `<s><span>${currencySymbol}</span><span>${compareValue}</span><span>${currencyCode}</span></s>`
      if (discountedPriceEl && salePrice) {
        const saleValue = splitPrice(salePrice)
        discountedPriceEl.innerHTML = `<span>${currencySymbol}</span><span>${saleValue}</span><span>${currencyCode}</span>`
      }
    } else {
      // No discount: show sale/current price in original field, clear discounted
      if (originalPriceEl && salePrice) {
        const saleValue = splitPrice(salePrice)
        originalPriceEl.innerHTML = `<span>${currencySymbol}</span><span>${saleValue}</span><span>${currencyCode}</span>`
      }
      if (discountedPriceEl) {
        discountedPriceEl.innerHTML = ''
      }
    }

    if (!this._pricePaintLogged) {
      this._pricePaintLogged = true
      this.perfLog('first one-time price rendered', {
        variantId: clickedButton.getAttribute('data-variant-id') || null,
      })
    }
  }

  /**
   * Find the product form and update the variant ID input
   * @param {string} formId - The form ID to search for
   * @param {string} variantId - The variant ID to set
   */
  static updateProductForm(formId, variantId) {
    // Find the form using the form ID
    const productForm = document.querySelector(
      `form[id="${formId}"], form[data-form-id="${formId}"]`
    )

    if (!productForm) {
      console.error(`Product form not found with ID: ${formId}`)
      return
    }

    // Find the variant ID input within the form
    const variantInput = productForm.querySelector(
      '.product-variant-id, input[name="id"]'
    )

    if (!variantInput) {
      console.error('Product variant ID input not found in form')
      return
    }

    // Update the input value
    variantInput.value = variantId

    // Dispatch a change event to notify other scripts
    variantInput.dispatchEvent(new Event('change', { bubbles: true }))
  }

  /**
   * Fetch subscription configuration data from the current page
   * Returns shopifyConfig, formattedPrices, and configs
   */
  async fetchSubscriptionData() {
    const fetchStart = performance.now()
    this.perfLog('fetchSubscriptionData start')

    try {
      const pricingHydrationEnabled =
        this.getAttribute('data-enable-pricing-hydration') === 'true'
      const hydrationProductUrl = this.getAttribute('data-product-url')

      // For selected-product sections, hydrate from the target product URL.
      // PDP keeps the existing current-page behavior.
      let data = null
      if (pricingHydrationEnabled && hydrationProductUrl) {
        const externalStart = performance.now()
        this.perfLog('fetch external Recharge config start', {
          url: hydrationProductUrl,
        })
        data = await getProductRechargeData(hydrationProductUrl)
        this.perfLog('fetch external Recharge config end', {
          durationMs: Number((performance.now() - externalStart).toFixed(1)),
          hasData: !!data,
        })
      }

      if (!data) {
        const currentStart = performance.now()
        this.perfLog('fetch current-page Recharge config start')
        data = await getProductRechargeData()
        this.perfLog('fetch current-page Recharge config end', {
          durationMs: Number((performance.now() - currentStart).toFixed(1)),
          hasData: !!data,
        })
      }

      if (data) {
        // Store the data for use in the widget
        this.subscriptionData = {
          shopifyConfig: data.shopifyConfig,
          formattedPrices: data.formattedPrices,
          configs: data.configs,
        }
      }

      // Always attempt initial paint so one-time-only products show pricing
      // even when Recharge data is unavailable.
      this.setInitialPrices()
      this.perfLog('setInitialPrices complete', {
        totalFetchMs: Number((performance.now() - fetchStart).toFixed(1)),
      })
      this.setPricingHydratedState(true)

      if (data) {
        this.perfLog('fetchSubscriptionData success', {
          totalMs: Number((performance.now() - fetchStart).toFixed(1)),
          source: data.source || 'unknown',
        })
        this.syncAddToCartReadiness('subscription data loaded')
        return this.subscriptionData
      }

      this.perfLog('fetchSubscriptionData complete with no data', {
        totalMs: Number((performance.now() - fetchStart).toFixed(1)),
      })
      this.syncAddToCartReadiness('subscription data unavailable')
    } catch (err) {
      console.error('Failed to fetch subscription data:', err)
      // Keep one-time pricing visible even when subscription data fetch fails.
      this.setInitialPrices()
      this.setPricingHydratedState(true)
      this.perfLog('fetchSubscriptionData error', {
        totalMs: Number((performance.now() - fetchStart).toFixed(1)),
      })
      this.syncAddToCartReadiness('subscription data error')
      return null
    }
  }

  /**
   * Set initial prices for the first selected button in each group
   */
  setInitialPrices() {
    // Subscription group
    const subsBtn =
      this.querySelector(
        '.variant-buttons-subs button[data-selected="true"]'
      ) ||
      this.querySelector(
        '.variant-buttons-subs button[data-variant-id]:not([style*="display: none"])'
      )
    if (subsBtn) {
      const variantId = subsBtn.getAttribute('data-variant-id')
      this.updatePriceFields(
        subsBtn,
        variantId,
        this.querySelector('.mitoq-subscription')
      )
    }
    // One-time group
    const onceBtn =
      this.querySelector(
        '.variant-buttons-once button[data-selected="true"]'
      ) ||
      this.querySelector(
        '.variant-buttons-once button[data-variant-id]:not([style*="display: none"])'
      )
    if (onceBtn) {
      this.updateOneTimePriceFields(
        onceBtn,
        this.querySelector('.mitoq-one-time')
      )
    }

    this.perfLog('initial price paint attempted', {
      hasSubscriptionButton: !!subsBtn,
      hasOneTimeButton: !!onceBtn,
    })
  }

  /**
   * Update the frequency heading in .mitoq-subscription based on the variant name
   * @param {HTMLElement} clickedButton
   * @param {HTMLElement} container
   */
  // eslint-disable-next-line class-methods-use-this
  updateFreqHeading(clickedButton, container) {
    const freqHeading = container.querySelector('[data-freq-heading]')
    if (!freqHeading) return
    const variantName = clickedButton.getAttribute('data-variant-name') || ''
    // Only for SUBS
    if (!variantName.startsWith('SUBS')) {
      freqHeading.textContent = ''
      return
    }
    // Parse frequency part
    const parts = variantName.split('-')
    if (parts.length < 2) {
      freqHeading.textContent = ''
      return
    }
    const freqPart = parts[1].toUpperCase()
    let freqString = ''
    if (freqPart === '1M') {
      freqString = 'PAID MONTHLY'
    } else if (freqPart.endsWith('M')) {
      const num = parseInt(freqPart)
      if (!Number.isNaN(num)) {
        freqString = `PAID EVERY ${num} MONTHS`
      }
    }
    freqHeading.textContent = freqString
  }

  /**
   * Update the schedule label in .mitoq-subscription based on the variant name
   * @param {HTMLElement} clickedButton
   * @param {HTMLElement} container
   * @param {boolean} isActive
   */
  updateScheduleLabel(clickedButton, container, isActive) {
    const labelEl = container.querySelector('[data-schedule-label]')
    if (!labelEl) return
    // Store the default value if not already
    if (!this._defaultScheduleLabel) {
      this._defaultScheduleLabel =
        labelEl.getAttribute('data-schedule-label') || labelEl.textContent
    }
    if (!isActive || !clickedButton) {
      labelEl.textContent = this._defaultScheduleLabel
      return
    }
    const variantName = clickedButton.getAttribute('data-variant-name') || ''
    if (!variantName.startsWith('SUBS')) {
      labelEl.textContent = this._defaultScheduleLabel
      return
    }
    const parts = variantName.split('-')
    if (parts.length < 2) {
      labelEl.textContent = this._defaultScheduleLabel
      return
    }
    const freqPart = parts[1].toUpperCase()
    let labelString = ''
    if (freqPart === '1M') {
      labelString = 'Sent every month'
    } else if (freqPart.endsWith('M')) {
      const num = parseInt(freqPart)
      if (!Number.isNaN(num)) {
        labelString = `Sent every ${num} months`
      }
    }
    labelEl.textContent = labelString || this._defaultScheduleLabel
  }

  /**
   * Show the default badge for each ms-method when closed, with the highest discount among its variants
   */
  showDefaultMethodBadges() {
    // Subscription method
    const subsMethod = this.querySelector('.mitoq-subscription.ms-method')
    if (subsMethod && subsMethod.getAttribute('data-is-active') === 'false') {
      const badgeEl = subsMethod.querySelector('[data-badge]')
      let maxDiscount = 0
      if (this.subscriptionData && this.subscriptionData.formattedPrices) {
        Object.keys(this.subscriptionData.formattedPrices).forEach(
          (variantId) => {
            const priceData = this.subscriptionData.formattedPrices[variantId]
            if (priceData && priceData.sellingPlans) {
              Object.keys(priceData.sellingPlans).forEach((planId) => {
                const plan = priceData.sellingPlans[planId]
                if (plan && plan.discountValue > maxDiscount) {
                  maxDiscount = plan.discountValue
                }
              })
            }
          }
        )
      }
      if (badgeEl) {
        badgeEl.textContent =
          maxDiscount > 0 ? `Save up to ${maxDiscount}%` : ''
      }
      subsMethod.setAttribute(
        'data-has-discount',
        maxDiscount > 0 ? 'true' : 'false'
      )
    }
    // One-time method
    const onceMethod = this.querySelector('.mitoq-one-time.ms-method')
    if (onceMethod && onceMethod.getAttribute('data-is-active') === 'false') {
      const badgeEl = onceMethod.querySelector('[data-one-time-badge]')
      let maxDiscount = 0
      const buttons = onceMethod.querySelectorAll('button[data-variant-id]')
      buttons.forEach((btn) => {
        const price = Number(btn.getAttribute('data-variant-price'))
        const compare = Number(btn.getAttribute('data-variant-price-compare'))
        if (compare > price) {
          const discount = Math.round((1 - price / compare) * 100)
          if (discount > maxDiscount) maxDiscount = discount
        }
      })
      if (badgeEl) {
        badgeEl.textContent =
          maxDiscount > 0 ? `Save up to ${maxDiscount}%` : ''
      }
      onceMethod.setAttribute(
        'data-has-discount',
        maxDiscount > 0 ? 'true' : 'false'
      )
    }
  }

  /**
   * Apply user type logic (B2B, wholesale, etc.) and filter available variants
   */
  applyUserTypeLogic() {
    // Read data attributes
    const isB2B = this.getAttribute('data-is-b2b') === 'true'
    const userType = this.getAttribute('data-user-type') || 'regular'
    let variantTitles = []
    try {
      variantTitles = JSON.parse(
        this.getAttribute('data-variant-titles') || '[]'
      )
    } catch (e) {
      // fallback: leave array empty
    }

    const subMethod = this.querySelector('.mitoq-subscription.ms-method')
    const onceMethod = this.querySelector('.mitoq-one-time.ms-method')

    // Filter variants based on user type restrictions
    this.filterVariantsByUserType(userType)

    if (isB2B) {
      // Always hide subscription for B2B, show only one-time
      if (subMethod) {
        subMethod.style.display = 'none'
        subMethod.setAttribute('data-is-active', 'false')
      }
      if (onceMethod) {
        onceMethod.setAttribute('data-is-active', 'true')
        onceMethod.style.display = ''
      }
      return
    }

    // For non-B2B, check for SUBS variants that are still available after filtering
    const remainingVariantTitles = this.getRemainingVariantTitles()
    const hasSubscriptionVariants = remainingVariantTitles.some((title) =>
      /^SUBS/i.test(title)
    )

    if (!hasSubscriptionVariants) {
      if (subMethod) {
        subMethod.style.display = 'none'
      }
      if (onceMethod) {
        onceMethod.setAttribute('data-is-active', 'true')
        onceMethod.style.display = ''
      }
    } else {
      if (subMethod) {
        subMethod.style.display = ''
      }
      if (onceMethod) {
        onceMethod.style.display = ''
      }
    }
  }

  /**
   * Filter variant buttons based on user type restrictions
   * @param {string} userType - The user type to filter for
   */
  filterVariantsByUserType(userType) {
    const allowedVariants = this.getAllowedVariants(userType)

    // If no restrictions are set, show all variants
    if (allowedVariants.length === 0) {
      return
    }

    // Hide buttons for variants that are not allowed
    const allButtons = this.querySelectorAll('button[data-variant-id]')
    let hasVisibleSubsButtons = false
    let hasVisibleOnceButtons = false

    allButtons.forEach((button) => {
      const variantName = button.getAttribute('data-variant-name') || ''
      const isAllowed = this.validateVariantAccess(variantName, userType)

      if (!isAllowed) {
        button.style.display = 'none'
        console.log(
          `Hiding variant '${variantName}' for user type '${userType}'`
        )
      } else {
        button.style.display = ''

        // Track which types of buttons are still visible
        if (button.closest('.variant-buttons-subs')) {
          hasVisibleSubsButtons = true
        }
        if (button.closest('.variant-buttons-once')) {
          hasVisibleOnceButtons = true
        }
      }
    })

    // Hide entire method sections if they have no visible variants
    const subMethod = this.querySelector('.mitoq-subscription.ms-method')
    const onceMethod = this.querySelector('.mitoq-one-time.ms-method')

    if (!hasVisibleSubsButtons && subMethod) {
      subMethod.style.display = 'none'
      subMethod.setAttribute('data-is-active', 'false')
      console.log('Hiding subscription method - no allowed variants')
    }

    if (!hasVisibleOnceButtons && onceMethod) {
      onceMethod.style.display = 'none'
      onceMethod.setAttribute('data-is-active', 'false')
      console.log('Hiding one-time method - no allowed variants')
    }

    // If only one method is visible, make it active
    if (hasVisibleOnceButtons && !hasVisibleSubsButtons && onceMethod) {
      onceMethod.setAttribute('data-is-active', 'true')
      if (subMethod) {
        subMethod.setAttribute('data-is-active', 'false')
      }
    } else if (hasVisibleSubsButtons && !hasVisibleOnceButtons && subMethod) {
      subMethod.setAttribute('data-is-active', 'true')
      if (onceMethod) {
        onceMethod.setAttribute('data-is-active', 'false')
      }
    }
  }

  /**
   * Get the variant titles that remain visible after filtering
   * @returns {Array<string>} Array of remaining variant titles
   */
  getRemainingVariantTitles() {
    const visibleButtons = this.querySelectorAll(
      'button[data-variant-id]:not([style*="display: none"])'
    )
    return Array.from(visibleButtons).map(
      (button) => button.getAttribute('data-variant-name') || ''
    )
  }

  /**
   * Handle B2B specific logic for add to cart button and selling plan clearing
   */
  handleB2BAutoSelection() {
    const isB2B = this.getAttribute('data-is-b2b') === 'true'
    if (!isB2B) return

    const formId = this.getAttribute('data-form-id')
    const productForm = document.querySelector(
      `form[id="${formId}"], form[data-form-id="${formId}"]`
    )

    if (!productForm) return

    const selectedButton = this.getSelectedButtonForSubmit() || null
    const selectedVariantId = selectedButton?.getAttribute('data-variant-id')

    if (selectedVariantId) {
      MitoqSubscriptionWidget.updateProductForm(formId, selectedVariantId)
    }

    this.syncSellingPlanInputForSelection(selectedButton)
  }

  /**
   * Disable add-to-cart button during initialization
   */
  disableAddToCartButton() {
    const formId = this.getAttribute('data-form-id')
    const productForm = document.querySelector(
      `form[id="${formId}"], form[data-form-id="${formId}"]`
    )

    if (!productForm) {
      console.warn(
        '⚠️ MitoQ Subscription: Product form not found for disabling add-to-cart'
      )
      return
    }

    const addToCartButton = productForm.querySelector('.product-form__submit')
    if (addToCartButton) {
      // Button already has is-loading class from Liquid, just ensure it's disabled
      addToCartButton.disabled = true
      // Spinner is already visible from Liquid (no hidden class initially)
      console.log(
        '🚫 MitoQ Subscription: Add-to-cart button disabled during loading'
      )
    }

    // Also disable sticky add-to-cart if it exists
    const stickyButton = document.querySelector('[data-add-to-cart]')
    if (stickyButton) {
      stickyButton.disabled = true
      const spinner = stickyButton.querySelector('.loading__spinner')
      if (spinner) {
        spinner.classList.remove('hidden')
      }
    }
  }

  /**
   * Enable add-to-cart button after initialization is complete
   */
  enableAddToCartButton() {
    const formId = this.getAttribute('data-form-id')
    const productForm = document.querySelector(
      `form[id="${formId}"], form[data-form-id="${formId}"]`
    )

    if (!productForm) {
      console.warn(
        '⚠️ MitoQ Subscription: Product form not found for enabling add-to-cart'
      )
      return
    }

    const addToCartButton = productForm.querySelector('.product-form__submit')
    if (addToCartButton) {
      addToCartButton.disabled = false
      addToCartButton.classList.remove('is-loading')
      // Hide loading spinner by adding hidden class back
      const spinner = addToCartButton.querySelector('.loading__spinner')
      if (spinner) {
        spinner.classList.add('hidden')
      }
      const elapsedMs =
        this._perfStart !== null
          ? `${(performance.now() - this._perfStart).toFixed(1)}ms`
          : 'n/a'
      console.log(
        `✅ MitoQ Subscription: Add-to-cart button enabled (t+${elapsedMs})`
      )
    }

    // Also enable sticky add-to-cart if it exists
    const stickyButton = document.querySelector('[data-add-to-cart]')
    if (stickyButton) {
      stickyButton.disabled = false
      const spinner = stickyButton.querySelector('.loading__spinner')
      if (spinner) {
        spinner.classList.add('hidden')
      }
    }
  }

  /**
   * Centralized auto-selection function that handles both UI and programmatic updates
   * @param {Object} options - Selection options
   * @param {string} options.variantId - Specific variant ID to select
   * @param {string} options.method - Method to activate ('subscription' or 'onetime')
   * @param {boolean} options.force - Force selection even if already selected
   * @param {boolean} options.silent - Don't dispatch sync events (for internal use)
   */
  autoSelectVariant(options = {}) {
    const { variantId, method, force = false, silent = false } = options

    // Step 1: Determine which method to activate
    let targetMethod = null
    if (method) {
      targetMethod =
        method === 'subscription'
          ? this.querySelector('.mitoq-subscription.ms-method')
          : this.querySelector('.mitoq-one-time.ms-method')
    } else if (variantId) {
      // Find method based on variant button location
      const variantBtn = this.querySelector(
        `button[data-variant-id='${variantId}']`
      )
      if (variantBtn) {
        targetMethod =
          variantBtn.closest('.mitoq-subscription.ms-method') ||
          variantBtn.closest('.mitoq-one-time.ms-method')
      }
    } else {
      // Default: find first visible method
      targetMethod =
        this.querySelector(
          '.mitoq-subscription.ms-method:not([style*="display: none"])'
        ) ||
        this.querySelector(
          '.mitoq-one-time.ms-method:not([style*="display: none"])'
        )
    }

    if (!targetMethod) {
      console.warn('No target method found for auto-selection')
      return false
    }

    // Step 2: Activate the method if needed
    const isCurrentlyActive =
      targetMethod.getAttribute('data-is-active') === 'true'
    if (!isCurrentlyActive || force) {
      // Deactivate all methods
      this.querySelectorAll('.ms-method').forEach((meth) => {
        meth.setAttribute('data-is-active', 'false')
      })
      // Activate target method
      targetMethod.setAttribute('data-is-active', 'true')
    }

    // Step 3: Find and select the variant
    let targetButton = null
    if (variantId) {
      targetButton = targetMethod.querySelector(
        `button[data-variant-id='${variantId}']`
      )
      if (targetButton && targetButton.style.display === 'none') {
        targetButton = null
      }
    } else {
      // Select first available variant in the method
      targetButton = targetMethod.querySelector(
        'button[data-variant-id]:not([style*="display: none"])'
      )
    }

    if (!targetButton) {
      console.warn('No target variant button found for auto-selection')
      return false
    }

    // Step 4: Check if already selected
    const isAlreadySelected =
      targetButton.getAttribute('data-selected') === 'true'
    if (isAlreadySelected && !force) {
      const formId = this.getAttribute('data-form-id')
      const productForm = document.querySelector(
        `form[id="${formId}"], form[data-form-id="${formId}"]`
      )
      const currentFormVariantId = productForm
        ?.querySelector('.product-variant-id, input[name="id"]')
        ?.value
      const targetVariantId = targetButton.getAttribute('data-variant-id')

      if (currentFormVariantId === targetVariantId) {
        return true // Already selected and form is in sync
      }
    }

    // Step 5: Perform the selection (UI + programmatic)
    this.performVariantSelection(targetButton, { silent })

    return true
  }

  /**
   * Internal method to perform the actual variant selection
   * @param {HTMLElement} button - The variant button to select
   * @param {Object} options - Options for selection
   * @param {boolean} options.silent - Don't dispatch sync events
   */
  performVariantSelection(button, options = {}) {
    const { silent = false } = options
    const variantId = button.getAttribute('data-variant-id')
    const variantName = button.getAttribute('data-variant-name') || ''
    const formId = this.getAttribute('data-form-id')

    if (!variantId || !formId) {
      console.error('Missing variant ID or form ID for selection')
      return
    }

    // Update URL
    const url = new URL(window.location.href)
    url.searchParams.set('variant', variantId)
    window.history.replaceState({}, '', url)

    // Update button selection states
    this.updateButtonSelection(button)

    // Update UI based on method type
    const isSubs = button.closest('.variant-buttons-subs') !== null
    const isOnce = button.closest('.variant-buttons-once') !== null
    let container = null

    if (isSubs) {
      container = this.querySelector('.mitoq-subscription')
      this.updatePriceFields(button, variantId, container)
      this.updateFreqHeading(button, container)
      this.updateScheduleLabel(button, container, true)
    } else if (isOnce) {
      container = this.querySelector('.mitoq-one-time')
      this.updateOneTimePriceFields(button, container)
    }

    // Update image
    this.updateImage(button)

    // Update form
    MitoqSubscriptionWidget.updateProductForm(formId, variantId)
    this.syncSellingPlanInputForSelection(button)

    // Dispatch sync event unless silent
    if (!silent) {
      const methodType = isSubs ? 'subscription' : 'onetime'
      const syncEvent = new CustomEvent('mitoq-subscription-sync', {
        detail: { method: methodType, variantId },
      })
      window.dispatchEvent(syncEvent)
    }

    this.syncAddToCartReadiness('programmatic variant selection')
  }
}

export default function doMitoqSubscription() {
  if (customElements.get('mitoq-subscription')) return

  customElements.define('mitoq-subscription', MitoqSubscriptionWidget)
}
